import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Mail, Phone, Link as LinkIcon, Briefcase, Calendar, MapPin, Building2,
  Star, Tag, User, FileText, CalendarPlus, AlertTriangle, UserCheck, CheckCircle2,
  Plus, ArrowRight, ArrowRightLeft, CalendarClock, Users, MessagesSquare,
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import InterviewScheduleDialog from '../../ats-interviews/InterviewScheduleDialog';
import ReferenceCheckFormDrawer from '../ReferenceCheckFormDrawer';
import ApplicationStageMoveDialog from '../ApplicationStageMoveDialog';
import ApplicationStatusChangeDialog from '../ApplicationStatusChangeDialog';
import {
  resolveCanManage,
  canMoveStage as canMoveStageOf,
  visibleTransitions,
  canScheduleInterview as canScheduleInterviewOf,
  openInterviewsOf,
} from '../applicationActions';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useApplicationDebriefs, DECISION_TONE } from '../../ats-interviews/debrief/debriefModel';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 8px 0 32px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 20px 22px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 14px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  font-size: 14px;
  padding: 10px 0;
  border-top: 1px solid #F2F4F7;
  &:first-of-type { border-top: 0; padding-top: 0; }
`;

const RowIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
  margin-top: 2px;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 2px;
`;

const RowValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
  a { color: ${({ theme }) => theme.colors.primary.main}; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $tone }) => $tone === 'green' ? '#E8F8F0'
    : $tone === 'red' ? '#FEE6E6'
    : $tone === 'amber' ? '#FFF5E1'
    : $tone === 'blue' ? '#E8F4FD'
    : '#EEF1F4'};
  color: ${({ $tone }) => $tone === 'green' ? '#1A6E38'
    : $tone === 'red' ? '#A02525'
    : $tone === 'amber' ? '#8A5500'
    : $tone === 'blue' ? '#024F87'
    : '#3E4A5A'};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const TagPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #F2F6FA;
  border-radius: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Empty = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-style: italic;
`;

const CardActions = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #F2F4F7;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const Skeleton = styled.div`
  padding: 40px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const WarnBanner = styled.div`
  background: #FFF5E1;
  border: 1px solid #F0C36D;
  border-left: 4px solid #E8A317;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
`;

const WarnIcon = styled.div`
  color: #B7791F;
  flex-shrink: 0;
  margin-top: 2px;
`;

const WarnTitle = styled.div`
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: #7A5200;
  margin-bottom: 4px;
`;

const WarnText = styled.div`
  font-size: 13px;
  color: #7A5200;
`;

const WarnList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 13px;
  color: #7A5200;
  li { margin: 2px 0; }
`;

// Contextual prompt shown only while the candidate sits in a reference_check
// stage. Bridges the pipeline stage to the (otherwise easy-to-miss) References
// tab where the actual reference form lives.
const RefCard = styled.section`
  background: #EFF6FF;
  border: 1px solid #BFD9F2;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.main};
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
`;

const RefHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RefBody = styled.div`
  flex: 1;
  min-width: 220px;
  font-size: 13px;
`;

const RefReady = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #1A6E38;
`;

const RefPending = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #8A5500;
`;

const RefActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// Planlanmış mülakatlar bölümü — iki kolonlu ızgarada TAM GENİŞLİK kaplar;
// satırlar aday/başvuru kartlarıyla aynı `Row` dilini kullanır, yeni bir tasarım
// dili YOKTUR.
const WideCard = styled(Card)`
  grid-column: 1 / -1;
`;

const ClickRow = styled(Row)`
  cursor: pointer;
  align-items: center;
  border-radius: 6px;
  &:hover { background: #F7FAFD; }
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 4px;
`;

// "Bu başvuruda zaten planlanmış bir mülakat var." — ENGELLEME DEĞİL, bilgilendirme:
// ikinci tur meşru olabilir, ama İK'nın farkında olmadan ikinci kez planlaması
// meşru değil.
const InlineHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8A5500;
`;

// Mülakat satırındaki debrief rozeti + bağlantısı. Satırın kendisi mülakat
// listesine götürür; bu düğme tıklamayı YUTAR ve debrief sayfasını açar.
const DebriefLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  &:hover span { text-decoration: underline; }
`;

const TERMINAL_NEGATIVE = ['disqualified', 'rejected', 'withdrawn'];

const INTERVIEW_STATUS_TONE = {
  scheduled: 'blue',
  completed: 'green',
  cancelled: 'neutral',
  rescheduled: 'amber',
  no_show: 'red',
};

const STATUS_TONE = {
  active: 'blue',
  on_hold: 'amber',
  offered: 'blue',
  accepted: 'green',
  rejected: 'red',
  withdrawn: 'neutral',
  disqualified: 'red',
};

const fmtDate = (d, lang) => {
  if (!d) return null;
  try { return new Date(d).toLocaleString(lang || 'tr'); } catch { return null; }
};

const safeArr = (jsonOrArr) => {
  if (!jsonOrArr) return [];
  if (Array.isArray(jsonOrArr)) return jsonOrArr;
  try { const v = JSON.parse(jsonOrArr); return Array.isArray(v) ? v : []; } catch { return []; }
};

export default function ApplicationOverviewTab({
  application, candidate, interviews, onInterviewsChanged,
  onNavigateToReferences, onApplicationChanged,
}) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();
  const canWriteAts = canWrite ? canWrite('AT') : false;
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Durum aksiyonu (Devam Et / Beklemeye Al / Diskalifiye Et / Aday Vazgeçti) —
  // liste sayfasındaki satır menüsüyle AYNI kümeyi çizer.
  const [statusDialog, setStatusDialog] = useState({ open: false, transition: null });
  const [priorApps, setPriorApps] = useState([]);
  const [references, setReferences] = useState(null);
  // Guided reference-stage flow: add a reference inline, then advance the stage —
  // all from this tab, so the form lives where the user manages the stage.
  const [refFormOpen, setRefFormOpen] = useState(false);
  const [moveDialog, setMoveDialog] = useState({ open: false, preselectNext: false });
  const [refReload, setRefReload] = useState(0);
  // Panel debrief durumu (SPEC F4): mülakat başına kayıt + şablonun zorunluluğu.
  // `pending` = tamamlanmış + debrief-zorunlu + kaydı olmayan → sunucunun
  // `ats.application.debriefRequired` kapısının aynası (ileri aşama/teklif 409).
  const debriefs = useApplicationDebriefs(application?.id, interviews);

  // Re-application warning: surface this person's OTHER applications (across all
  // postings) so a recruiter notices a previously-in-process — or disqualified —
  // candidate re-applying. Matched by EMAIL, not candidateId: each re-apply spawns
  // a new AtsCandidate row, so a candidateId filter would miss the prior ones.
  const candidateEmail = candidate?.email;
  const currentAppId = application?.id;
  useEffect(() => {
    if (!candidateEmail || !currentAppId) { setPriorApps([]); return undefined; }
    let cancelled = false;
    api.get(`/v1/ats-applications?candidateEmail[eq]=${encodeURIComponent(candidateEmail)}&pageSize=50`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data;
        const list = Array.isArray(d) ? d : (d?.data || []);
        setPriorApps(list.filter((a) => a.id !== currentAppId));
      })
      .catch(() => { if (!cancelled) setPriorApps([]); });
    return () => { cancelled = true; };
  }, [candidateEmail, currentAppId]);

  // When the candidate sits in a reference_check stage, pull the reference checks
  // so we can show whether the advance-gate (≥1 completed) is satisfied and lead
  // the user to the form. Skipped entirely for any other stage.
  const isReferenceStage = application?.currentStageType === 'reference_check';
  useEffect(() => {
    if (!isReferenceStage || !currentAppId) { setReferences(null); return undefined; }
    let cancelled = false;
    api.get(`/v1/ats-reference-checks/for-application/${currentAppId}`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data;
        setReferences(Array.isArray(d) ? d : (d?.data || []));
      })
      .catch(() => { if (!cancelled) setReferences([]); });
    return () => { cancelled = true; };
  }, [isReferenceStage, currentAppId, refReload]);

  if (!application) return <Skeleton>{t('common.loading', { defaultValue: 'Yükleniyor...' })}</Skeleton>;

  const hasDisqualifiedPrior = priorApps.some((a) => TERMINAL_NEGATIVE.includes(a.status));
  const referenceCompletedCount = (references || []).filter((r) => r.status === 'completed').length;

  const statusTone = STATUS_TONE[application.status] || 'neutral';
  const tags = safeArr(application.tagsJson);
  // Süreç aksiyonları (aşama/durum) yalnız İK'da — Hiring Manager salt-izler;
  // backend bunu application.canManage=false ile bildirir. Mülakat planlama İK
  // veya ilana atanmış koordinatörde (application.canSchedule).
  //
  // Kurallar applicationActions.js'te TEK KAYNAK: liste satır menüsü ile bu şerit
  // aynı kümeyi üretir. (Eskiden bu dosya aşama taşımayı `on_hold` başvuruda da
  // çiziyordu — BE 409 `ats.application.cannotMoveOnHold` döndüğü için garanti
  // hataydı; liste sayfası ise yalnız `active` diyordu. İki kopya ayrışmıştı.)
  const canManage = resolveCanManage(application, { writable: canWriteAts });
  const canScheduleInterview = canScheduleInterviewOf(application, { writable: canWriteAts });
  const canMoveStage = canMoveStageOf(application, { canManage });
  const statusTransitions = visibleTransitions(application, { canManage });

  // Açık (planlanmış) mülakat varken ikinci bir mülakat planlamak genelde hatadır;
  // engellemiyoruz (ikinci tur meşru olabilir), yalnız görünür kılıyoruz.
  const interviewList = Array.isArray(interviews) ? interviews : [];
  const openInterviews = openInterviewsOf(interviewList);
  const goToInterviews = () => {
    const q = new URLSearchParams({ applicationId: application.id });
    if (application.candidateFullName) q.set('candidateName', application.candidateFullName);
    history.push(`/ats/interviews?${q}`);
  };

  return (
    <>
    {priorApps.length > 0 && (
      <WarnBanner>
        <WarnIcon><AlertTriangle size={20} /></WarnIcon>
        <div>
          <WarnTitle>{t('ats.application.reapplyWarning.title')}</WarnTitle>
          <WarnText>
            {t('ats.application.reapplyWarning.summary', { count: priorApps.length })}
            {hasDisqualifiedPrior ? ` ${t('ats.application.reapplyWarning.disqualifiedNote')}` : ''}
          </WarnText>
          <WarnList>
            {priorApps.slice(0, 5).map((a) => (
              <li key={a.id}>
                {(a.jobPostingTitle || '—')}
                {' — '}
                {t(`ats.application.status.${a.status}`, { defaultValue: a.status })}
                {a.appliedAt ? ` — ${fmtDate(a.appliedAt, i18n.language)}` : ''}
              </li>
            ))}
          </WarnList>
        </div>
      </WarnBanner>
    )}
    {debriefs.pending.length > 0 && (
      <WarnBanner data-role="ats-debrief-pending-banner">
        <WarnIcon><MessagesSquare size={20} /></WarnIcon>
        <div>
          <WarnTitle>{t('ats.debrief.applicationBanner.title')}</WarnTitle>
          <WarnText>{t('ats.debrief.applicationBanner.body', { count: debriefs.pending.length })}</WarnText>
          <CardActions style={{ borderTop: 0, paddingTop: 0, marginTop: 10 }}>
            {debriefs.pending.map((iv) => (
              <Button
                key={iv.id}
                variant="outline"
                data-role="ats-debrief-pending-link"
                onClick={() => history.push(`/ats/interviews/${iv.id}/debrief`)}
              >
                {t('ats.debrief.applicationBanner.open', {
                  type: t(`ats.interview.type.${iv.interviewType}`, { defaultValue: iv.interviewType }),
                  date: fmtDate(iv.scheduledStart, i18n.language) || '',
                })}
              </Button>
            ))}
          </CardActions>
        </div>
      </WarnBanner>
    )}
    {isReferenceStage && (
      <RefCard>
        <RefHead>
          <UserCheck size={18} />
          {t('ats.application.referenceStage.title')}
        </RefHead>
        <RefBody>
          {references != null && (
            referenceCompletedCount > 0
              ? <RefReady><CheckCircle2 size={14} />{t('ats.application.referenceStage.ready', { count: referenceCompletedCount })}</RefReady>
              : <RefPending><AlertTriangle size={14} />{t('ats.application.referenceStage.pending')}</RefPending>
          )}
        </RefBody>
        <RefActions>
          {canWriteAts && (
            <Button onClick={() => setRefFormOpen(true)}>
              <Plus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('ats.application.referenceStage.add')}
            </Button>
          )}
          {canManage && referenceCompletedCount > 0 && (
            <Button onClick={() => setMoveDialog({ open: true, preselectNext: true })}>
              <ArrowRight size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('ats.application.referenceStage.advance')}
            </Button>
          )}
          <Button variant="outline" onClick={() => onNavigateToReferences && onNavigateToReferences()}>
            <UserCheck size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {t('ats.application.referenceStage.manage')}
          </Button>
        </RefActions>
      </RefCard>
    )}
    <Container>
      {/* Candidate card */}
      <Card>
        <CardHeader><User size={14} />{t('ats.application.section.candidate')}</CardHeader>
        <Row>
          <RowIcon><User size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.fullName')}</RowLabel>
            <RowValue>{application.candidateFullName || <Empty>—</Empty>}</RowValue>
          </RowBody>
        </Row>
        <Row>
          <RowIcon><Mail size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.email')}</RowLabel>
            <RowValue>
              {application.candidateEmail
                ? <a href={`mailto:${application.candidateEmail}`}>{application.candidateEmail}</a>
                : <Empty>—</Empty>}
            </RowValue>
          </RowBody>
        </Row>
        <Row>
          <RowIcon><Phone size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.phone')}</RowLabel>
            <RowValue>
              {candidate?.phone
                ? <a href={`tel:${candidate.phone}`}>{candidate.phone}</a>
                : <Empty>—</Empty>}
            </RowValue>
          </RowBody>
        </Row>
        <Row>
          <RowIcon><LinkIcon size={16} /></RowIcon>
          <RowBody>
            <RowLabel>LinkedIn</RowLabel>
            <RowValue>
              {candidate?.linkedInUrl
                ? <a href={candidate.linkedInUrl} target="_blank" rel="noopener noreferrer">{candidate.linkedInUrl}</a>
                : <Empty>—</Empty>}
            </RowValue>
          </RowBody>
        </Row>
        {candidate?.location && (
          <Row>
            <RowIcon><MapPin size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.location')}</RowLabel>
              <RowValue>{candidate.location}</RowValue>
            </RowBody>
          </Row>
        )}
        {(candidate?.currentTitle || candidate?.currentEmployer) && (
          <Row>
            <RowIcon><Building2 size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.currentRole')}</RowLabel>
              <RowValue>
                {candidate?.currentTitle || ''}
                {candidate?.currentEmployer ? ` — ${candidate.currentEmployer}` : ''}
              </RowValue>
            </RowBody>
          </Row>
        )}
        {candidate?.coverLetter && (
          <Row>
            <RowIcon><FileText size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.coverLetter')}</RowLabel>
              <RowValue style={{ whiteSpace: 'pre-wrap' }}>{candidate.coverLetter}</RowValue>
            </RowBody>
          </Row>
        )}
      </Card>

      {/* Application card */}
      <Card>
        <CardHeader><Briefcase size={14} />{t('ats.application.section.application')}</CardHeader>
        <Row>
          <RowIcon><Briefcase size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.jobPosting')}</RowLabel>
            <RowValue>{application.jobPostingTitle || <Empty>—</Empty>}</RowValue>
          </RowBody>
        </Row>
        <Row>
          <RowIcon><Calendar size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.appliedAt')}</RowLabel>
            <RowValue>{fmtDate(application.appliedAt, i18n.language) || <Empty>—</Empty>}</RowValue>
          </RowBody>
        </Row>
        <Row>
          <RowIcon><Tag size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.status')}</RowLabel>
            <RowValue>
              <StatusBadge $tone={statusTone}>
                {t(`ats.application.status.${application.status}`, { defaultValue: application.status })}
              </StatusBadge>
            </RowValue>
          </RowBody>
        </Row>
        <Row>
          <RowIcon><Tag size={16} /></RowIcon>
          <RowBody>
            <RowLabel>{t('ats.application.field.currentStage')}</RowLabel>
            <RowValue>{application.currentStageName || <Empty>—</Empty>}</RowValue>
          </RowBody>
        </Row>
        {application.scoreAggregate != null && (
          <Row>
            <RowIcon><Star size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.score')}</RowLabel>
              <RowValue>{Number(application.scoreAggregate).toFixed(2)}</RowValue>
            </RowBody>
          </Row>
        )}
        {candidate?.source && (
          <Row>
            <RowIcon><Tag size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.source')}</RowLabel>
              <RowValue>{t(`ats.candidate.source.${candidate.source}`, { defaultValue: candidate.source })}</RowValue>
            </RowBody>
          </Row>
        )}
        {tags.length > 0 && (
          <Row>
            <RowIcon><Tag size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.tags')}</RowLabel>
              <RowValue>
                <Tags>{tags.map((tg, i) => <TagPill key={i}>{tg}</TagPill>)}</Tags>
              </RowValue>
            </RowBody>
          </Row>
        )}
        {application.disqualificationReason && (
          <Row>
            <RowIcon><Tag size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.disqualificationReason')}</RowLabel>
              {/* Kaskad kapanışlar (talep iptali/dolumu) buraya da sabit slug yazar
                  (position_cancelled / position_filled) — rejectionReason ile aynı slug haritası;
                  serbest metin gerekçe olduğu gibi kalır. */}
              <RowValue>
                {t(`ats.application.rejectionReasonSlug.${application.disqualificationReason}`, {
                  defaultValue: application.disqualificationReason,
                })}
              </RowValue>
            </RowBody>
          </Row>
        )}
        {application.rejectionReason && (
          <Row>
            <RowIcon><Tag size={16} /></RowIcon>
            <RowBody>
              <RowLabel>{t('ats.application.field.rejectionReason')}</RowLabel>
              {/* Kaskad kapanışlar sabit slug yazar (position_cancelled/position_filled) — çevirisi varsa göster */}
              <RowValue>
                {t(`ats.application.rejectionReasonSlug.${application.rejectionReason}`, {
                  defaultValue: application.rejectionReason,
                })}
              </RowValue>
            </RowBody>
          </Row>
        )}
        {(canScheduleInterview || canMoveStage || statusTransitions.length > 0) && (
          <CardActions>
            {canMoveStage && (
              <Button variant="outline" onClick={() => setMoveDialog({ open: true, preselectNext: false })}>
                <ArrowRightLeft size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {t('ats.application.action.moveStage')}
              </Button>
            )}
            {canScheduleInterview && (
              <Button variant="outline" onClick={() => setScheduleOpen(true)}>
                <CalendarPlus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {t('ats.interview.schedule.title')}
              </Button>
            )}
            {/* Durum aksiyonları listenin satır menüsündeydi: askıdaki bir başvuruya
                teklif yapmak için detaydan çıkıp listeye dönmek, satırı bulmak,
                "Devam Et" demek ve tekrar detaya girmek gerekiyordu. */}
            {statusTransitions.map((tr) => (
              <Button
                key={tr.action}
                variant="outline"
                data-role={`ats-status-action-${tr.action}`}
                onClick={() => setStatusDialog({ open: true, transition: tr })}
              >
                {t(tr.labelKey)}
              </Button>
            ))}
            {canScheduleInterview && openInterviews.length > 0 && (
              <InlineHint data-role="ats-open-interview-hint">
                <AlertTriangle size={14} />
                {t('ats.application.interviews.alreadyScheduled')}
              </InlineHint>
            )}
          </CardActions>
        )}
      </Card>

      {/* Planlanmış mülakatlar — bu veri eskiden YALNIZ "Aşama Geçmişi" sekmesindeydi;
          Genel Bakış hiç çekmiyordu ve "Mülakat Planla" düğmesi mevcut mülakattan
          habersiz çiziliyordu. */}
      {interviewList.length > 0 && (
        <WideCard data-role="ats-application-interviews">
          <CardHeader><CalendarClock size={14} />{t('ats.application.section.interviews')}</CardHeader>
          {interviewList.map((iv) => {
            const names = (iv.participants || [])
              .map((p) => p.name)
              .filter(Boolean);
            return (
              <ClickRow
                key={iv.id}
                role="button"
                tabIndex={0}
                data-role="ats-interview-row"
                onClick={goToInterviews}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToInterviews(); } }}
              >
                <RowIcon><CalendarClock size={16} /></RowIcon>
                <RowBody>
                  <RowValue>
                    {fmtDate(iv.scheduledStart, i18n.language) || <Empty>—</Empty>}
                    {' — '}
                    {t(`ats.interview.type.${iv.interviewType}`, { defaultValue: iv.interviewType })}
                  </RowValue>
                  <RowMeta>
                    <StatusBadge $tone={INTERVIEW_STATUS_TONE[iv.status] || 'neutral'}>
                      {t(`ats.interview.status.${iv.status}`, { defaultValue: iv.status })}
                    </StatusBadge>
                    <span>
                      <Users size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {names.length > 0
                        ? names.join(', ')
                        : t('ats.application.interviews.participantCount', { count: (iv.participants || []).length })}
                    </span>
                    {iv.location && <span><MapPin size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />{iv.location}</span>}
                    {iv.status === 'completed' && (() => {
                      const db = debriefs.byInterview[iv.id];
                      const needed = debriefs.required[iv.id] === true;
                      if (!db && !needed && !debriefs.loaded) return null;
                      return (
                        <DebriefLink
                          type="button"
                          data-role="ats-interview-debrief"
                          onClick={(e) => { e.stopPropagation(); history.push(`/ats/interviews/${iv.id}/debrief`); }}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <MessagesSquare size={12} />
                          {db ? (
                            <StatusBadge $tone={DECISION_TONE[db.decision] || 'neutral'}>
                              {t('ats.debrief.badge.decided', {
                                decision: t(`ats.debrief.decision.${db.decision}`, { defaultValue: db.decision }),
                              })}
                            </StatusBadge>
                          ) : needed ? (
                            <StatusBadge $tone="amber">{t('ats.debrief.badge.pending')}</StatusBadge>
                          ) : (
                            <span>{t('ats.debrief.action')}</span>
                          )}
                        </DebriefLink>
                      );
                    })()}
                  </RowMeta>
                </RowBody>
              </ClickRow>
            );
          })}
          <CardActions>
            <Button variant="outline" onClick={goToInterviews}>
              <ArrowRight size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {t('ats.application.interviews.viewAll')}
            </Button>
          </CardActions>
        </WideCard>
      )}
    </Container>
    <InterviewScheduleDialog
      open={scheduleOpen}
      application={application}
      onClose={() => setScheduleOpen(false)}
      onCreated={() => onInterviewsChanged && onInterviewsChanged()}
    />
    <ReferenceCheckFormDrawer
      open={refFormOpen}
      applicationId={application.id}
      reference={null}
      onClose={() => setRefFormOpen(false)}
      onSaved={() => setRefReload((p) => p + 1)}
    />
    <ApplicationStageMoveDialog
      open={moveDialog.open}
      application={application}
      preselectNext={moveDialog.preselectNext}
      onClose={() => setMoveDialog({ open: false, preselectNext: false })}
      onMoved={() => onApplicationChanged && onApplicationChanged()}
    />
    <ApplicationStatusChangeDialog
      open={statusDialog.open}
      application={application}
      transition={statusDialog.transition}
      onClose={() => setStatusDialog({ open: false, transition: null })}
      onChanged={() => onApplicationChanged && onApplicationChanged()}
    />
    </>
  );
}
